import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Chip,
  Grid,
  Divider,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  Home as HomeIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Description as DescriptionIcon,
  Edit as EditIcon,
  Print as PrintIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ResponsiveContainer from '../../components/layout/ResponsiveContainer';
import { vouchersApi } from '../../../lib/api';
import { useApi } from '../../../hooks/useApi';
import { useAppStore } from '../../../lib/store';
import { formatDate } from '../../../lib/dateUtils';

const typeColors = {
  receipt: 'success',
  payment: 'error',
  journal: 'info',
} as const;

const statusColors = {
  draft: 'default',
  posted: 'success',
  cancelled: 'error',
} as const;

export default function VoucherDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation(['finance', 'common']);
  const user = useAppStore((state) => state.user);
  const isManagerOrAdmin = user?.role === 'manager' || user?.role === 'admin';

  const { data: voucher, loading, error } = useApi(
    async () => {
      if (!id) throw new Error('No voucher ID provided');
      const response = await vouchersApi.getById(id);
      if (response.error) throw new Error(response.error.message);
      return response.data;
    },
    [id]
  );

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-SA', {
      style: 'currency',
      currency: voucher?.currency || 'SAR',
      minimumFractionDigits: 2,
    }).format(amount || 0);
  };

  if (loading) {
    return (
      <ResponsiveContainer>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      </ResponsiveContainer>
    );
  }

  if (error || !voucher) {
    return (
      <ResponsiveContainer>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error || t('common:somethingWentWrong')}
        </Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard/finance/vouchers')}
        >
          {t('common:back')}
        </Button>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer>
      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/dashboard/finance/vouchers')}
          variant="outlined"
        >
          {t('common:back')}
        </Button>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
            {voucher.voucher_number}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t(`voucherTypes.${voucher.voucher_type}`, voucher.voucher_type)}
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }} className="no-print">
          <Chip
            label={t(`voucherTypes.${voucher.voucher_type}`, voucher.voucher_type)}
            color={typeColors[voucher.voucher_type as keyof typeof typeColors] || 'default'}
          />
          <Chip
            label={t(`voucherStatuses.${voucher.status}`, voucher.status)}
            color={statusColors[voucher.status as keyof typeof statusColors] || 'default'}
          />
          <Button
            startIcon={<PrintIcon />}
            variant="outlined"
            onClick={() => window.print()}
          >
            {t('print', 'Print')}
          </Button>
          {isManagerOrAdmin && voucher.status === 'draft' && (
            <Button
              startIcon={<EditIcon />}
              variant="contained"
              onClick={() => navigate(`/dashboard/finance/vouchers/${id}/edit`)}
            >
              {t('common:edit')}
            </Button>
          )}
        </Box>
      </Box>

      {/* Print Styles */}
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .printable-area, .printable-area * {
              visibility: visible;
            }
            .printable-area {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              padding: 20px;
            }
            .no-print {
              display: none !important;
            }
            .print-only {
              display: block !important;
            }
            .MuiCard-root {
              box-shadow: none !important;
              border: 1px solid #ddd !important;
            }
          }
          .print-only {
            display: none;
          }
        `}
      </style>

      {/* Print Header - Only visible when printing */}
      <Box className="print-only" sx={{ textAlign: 'center', mb: 3, pb: 2, borderBottom: '2px solid #1976d2' }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          {t('companyName', 'Makeen Property Management')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t(`voucherTypes.${voucher.voucher_type}`, voucher.voucher_type)} {t('voucher', 'Voucher')}
        </Typography>
      </Box>

      <Box className="printable-area">
        <Grid container spacing={3}>
          {/* Main Voucher Card */}
          <Grid item xs={12} md={8}>
            <Card>
            <CardContent>
              {/* Voucher Header */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3 }}>
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <ReceiptIcon color="primary" />
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {t(`voucherTypes.${voucher.voucher_type}`, voucher.voucher_type)} {t('voucher', 'Voucher')}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {t('voucherNumber', 'Voucher Number')}: {voucher.voucher_number}
                  </Typography>
                </Box>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                    {formatCurrency(voucher.amount)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {voucher.currency || 'SAR'}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Description */}
              {voucher.description && (
                <Box sx={{ mb: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <DescriptionIcon fontSize="small" color="action" />
                    <Typography variant="subtitle2" color="text.secondary">
                      {t('description', 'Description')}
                    </Typography>
                  </Box>
                  <Typography variant="body1">
                    {voucher.description}
                  </Typography>
                </Box>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Amount Details */}
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1 }}>
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('amount', 'Amount')}
                    </Typography>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                      {formatCurrency(voucher.amount)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('currency', 'Currency')}
                    </Typography>
                    <Typography variant="h6" sx={{ fontWeight: 500 }}>
                      {voucher.currency || 'SAR'}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Sidebar */}
        <Grid item xs={12} md={4}>
          {/* Related Information */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t('relatedInformation', 'Related Information')}
              </Typography>

              {/* Tenant */}
              {voucher.tenant && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {t('tenant', 'Tenant')}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {voucher.tenant.first_name} {voucher.tenant.last_name}
                  </Typography>
                  {voucher.tenant.email && (
                    <Typography variant="body2" color="text.secondary">
                      {voucher.tenant.email}
                    </Typography>
                  )}
                  {voucher.tenant.phone_number && (
                    <Typography variant="body2" color="text.secondary">
                      {voucher.tenant.phone_number}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Property */}
              {voucher.property && (
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <HomeIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {t('property', 'Property')}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {voucher.property.title}
                  </Typography>
                  {voucher.property.address && (
                    <Typography variant="body2" color="text.secondary">
                      {voucher.property.address}
                      {voucher.property.city && `, ${voucher.property.city}`}
                    </Typography>
                  )}
                </Box>
              )}

              {/* Created By */}
              {voucher.created_by_user && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <PersonIcon fontSize="small" color="action" />
                    <Typography variant="body2" color="text.secondary">
                      {t('createdBy', 'Created By')}
                    </Typography>
                  </Box>
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    {voucher.created_by_user.first_name} {voucher.created_by_user.last_name}
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Timeline */}
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                {t('timeline', 'Timeline')}
              </Typography>

              {/* Created Date */}
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {t('createdAt', 'Created')}
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {formatDate(voucher.created_at)}
                </Typography>
              </Box>

              {/* Updated Date */}
              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <CalendarIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {t('lastUpdated', 'Last Updated')}
                  </Typography>
                </Box>
                <Typography variant="body1">
                  {formatDate(voucher.updated_at)}
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Print Signature Section - Only visible when printing */}
        <Grid item xs={12} className="print-only">
          <Box sx={{ mt: 4, pt: 4, borderTop: '1px solid #ddd' }}>
            <Grid container spacing={4}>
              <Grid item xs={6}>
                <Box sx={{ borderTop: '1px solid #333', pt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('signatureLine', 'Signature')}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Box sx={{ borderTop: '1px solid #333', pt: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    {t('dateLine', 'Date')}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              {t('authorizedSignatory', 'Authorized Signatory')}
            </Typography>
          </Box>
        </Grid>
      </Grid>
      </Box>
    </ResponsiveContainer>
  );
}
